import { is } from 'bpmn-js/lib/util/ModelUtil';
import { TextFieldEntry, isTextFieldEntryEdited } from '@bpmn-io/properties-panel';
import { useService } from 'bpmn-js-properties-panel';
import { html } from 'htm/preact';
import { getBpmnEventBus } from '../utils/bridge';

/**
 * Video group — shown only for Task elements.
 * Displays the assigned video timestamp and provides controls to
 * jump to the timestamp or remove it.
 */
export default function VideoGroup(element, injector) {
  const translate = injector.get('translate');

  if (!is(element, 'bpmn:Task')) {
    return null;
  }

  return {
    id: 'video',
    label: translate('Video'),
    entries: [
      {
        id: 'videoTimestamp',
        component: VideoTimestampEntry,
        isEdited: isTextFieldEntryEdited
      },
      {
        id: 'videoTimestampActions',
        component: VideoTimestampActions
      }
    ]
  };
}

// --- Timestamp display / edit ---
function VideoTimestampEntry(props) {
  const { element } = props;

  const modeling = useService('modeling');
  const translate = useService('translate');
  const debounce = useService('debounceInput');

  const getValue = () => {
    const bo = element.businessObject;
    const ts = (bo && typeof bo.get === 'function') ? bo.get('custom:videoTimestamp') : null;
    return ts || '';
  };

  const setValue = (value) => {
    modeling.updateModdleProperties(element, element.businessObject, {
      'custom:videoTimestamp': value || undefined
    });
  };

  return TextFieldEntry({
    element,
    id: 'videoTimestamp',
    label: translate('Video-Timestamp (Sekunden)'),
    getValue,
    setValue,
    debounce,
    description: translate('Zeitpunkt im Video (z.B. 65.30 = 01:05)')
  });
}

// --- Action buttons: Jump to Timestamp / Remove ---
function VideoTimestampActions(props) {
  const { element } = props;
  const bo = element.businessObject;
  const timestamp = (bo && typeof bo.get === 'function') ? bo.get('custom:videoTimestamp') : null;

  if (!timestamp) {
    return html`
      <div style="padding: 4px 12px; color: #888; font-size: 12px;">
        Kein Timestamp zugewiesen. Verwende den Video-Player, um einen Timestamp zuzuweisen.
      </div>
    `;
  }

  const formatTimestamp = (seconds) => {
    const s = Number.parseFloat(seconds);
    if (Number.isNaN(s)) return seconds;
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    const ms = Math.floor((s % 1) * 100);
    return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}.${String(ms).padStart(2, '0')}`;
  };

  const handleJump = () => {
    // Fire custom event to seek video player via module bridge
    try {
      const eventBus = getBpmnEventBus();
      if (eventBus) {
        eventBus.fire('videoPlayer.seekTo', { timestamp: Number.parseFloat(timestamp) });
      }
    } catch { /* eventBus not available */ }
  };

  return html`
    <div style="padding: 6px 12px; display: flex; flex-direction: column; gap: 6px;">
      <div style="display: flex; align-items: center; gap: 8px;">
        <span style="font-size: 16px;">⏱</span>
        <span style="font-weight: 600; font-size: 14px; color: #0d6efd;">
          ${formatTimestamp(timestamp)}
        </span>
      </div>
      <button
        class="bio-properties-panel-button"
        style="font-size: 12px; padding: 4px 10px; cursor: pointer;"
        onClick=${handleJump}
      >
        ▶ Zum Timestamp springen
      </button>
    </div>
  `;
}
